import { useCallback } from "react";

import { Flex, IconButton } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { ICheck, TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { openOngoingActivityModal } from "app/stores/useLayoutStore";
import { routes } from "config/routes";
import { CheckScanner } from "flows/Inventory/shared/components/CheckScanner";
import { ChecksList } from "flows/Inventory/shared/components/ChecksList";
import { ConnectionErrorModal } from "flows/Inventory/shared/components/ConnectionErrorModal";
import { TaskIsTakenModal } from "flows/Inventory/shared/components/TaskIsTakenModal";
import { useChecksList } from "flows/Inventory/shared/hooks/useChecksList";
import { shelfOptions, useFilteredChecks } from "flows/Inventory/shared/hooks/useFilteredChecks";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { SwipeablePageWrapper } from "shared/components/SwipeablePageWrapper";
import { IntlMessageId } from "shared/lang";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";
import { AscDscIcon } from "ui/AscDscIcon/AscDscIcon";
import { DropDown } from "ui/DropDown/DropDown";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { TitleM } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { useEoyChecksStore } from "../hooks/useEoyChecksStore";

interface BaseChecksListProps {
  taskType: TaskType;
  pageOrigin: PageName;
  title: IntlMessageId;
}

export function BaseChecksList({ taskType, pageOrigin, title }: BaseChecksListProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const timedActivityInstance = useTimedActivityStore((state) => state.timedActivityInstance);
  const isAutoScanActive = useEmployeeStore((state) => state.autoScan);

  const {
    rawChecks,
    refetchChecks,
    isLoadingChecks,
    isLoadingFetchNextCheckAndStartCheck,
    startCheck,
    isErrorModalOpen,
    isTaskIsTakenModalOpen,
    onCloseErrorModal,
    onCloseTaskIsTakenModal,
  } = useChecksList({
    type: [taskType],
    origin: pageOrigin,
  });

  const onSelectCheck = useCallback(
    (shelfId: string, priority: number) => {
      if (isNotNullNorUndefined(timedActivityInstance)) {
        openOngoingActivityModal();
        return;
      }
      startCheck(shelfId, priority);
    },
    [startCheck, timedActivityInstance],
  );

  const { selectedShelf, shelfSortOrder, setSelectedShelf, toggleShelfSortOrder, reset } =
    useEoyChecksStore(
      useShallow((state) => ({
        selectedShelf: state.selectedShelf,
        shelfSortOrder: state.shelfSortOrder,
        setSelectedShelf: state.setSelectedShelf,
        toggleShelfSortOrder: state.toggleShelfSortOrder,
        reset: state.reset,
      })),
    );

  const sortedAndGroupedChecks = useFilteredChecks(
    rawChecks as ICheck[],
    selectedShelf,
    shelfSortOrder,
    taskType,
  );

  return (
    <>
      <SwipeablePageWrapper
        refetch={refetchChecks}
        isFull
        justifyContent="flex-start"
        flexDirection="column"
        h="100%"
        isLoading={isLoadingChecks}
      >
        <NavigationHeader
          title={intl.formatMessage({
            id: title,
          })}
          onClickGoBack={() => {
            reset();
            navigate(routes.inventory.root);
          }}
        />
        <CheckScanner checksList={rawChecks} startCheck={startCheck} type={[taskType]} />
        <Flex
          justify="space-between"
          w="100%"
          gap="s100"
          px="s200"
          pb="s200"
          pt={isAutoScanActive ? 0 : "s200"}
        >
          <DropDown
            options={shelfOptions}
            value={selectedShelf}
            onInput={setSelectedShelf}
            filterMode={false}
            w="150px"
            h="40px"
            placement="top"
            listStyles={{ w: "150px" }}
            popoverStyles={{ mr: "0" }}
          />
          <IconButton
            size="md"
            variant="subtle"
            bg="white"
            aria-label="Toggle shelf sort order"
            icon={<AscDscIcon height="24px" width="24px" sortState={shelfSortOrder} />}
            onClick={toggleShelfSortOrder}
          />
        </Flex>
        {sortedAndGroupedChecks.length > 0 ? (
          <Flex h="100%" direction="column" w="100%" overflowY="scroll">
            <ChecksList items={sortedAndGroupedChecks} onSelectCheck={onSelectCheck} />
          </Flex>
        ) : (
          <Flex direction="column" w="full" h="70%" justify="center" textAlign="center" px="s100">
            <TitleM>
              {intl.formatMessage({
                id: "generic.warnings.no-results-found",
              })}
            </TitleM>
          </Flex>
        )}
      </SwipeablePageWrapper>
      <TaskIsTakenModal
        isOpen={isTaskIsTakenModalOpen}
        onClose={onCloseTaskIsTakenModal}
        onRefresh={refetchChecks}
      />
      <ConnectionErrorModal
        isOpen={isErrorModalOpen}
        onClose={onCloseErrorModal}
        onRefresh={refetchChecks}
      />
      <SpinnerModal isOpen={isLoadingFetchNextCheckAndStartCheck} />
    </>
  );
}
